import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Plus, Trash2, Stethoscope, ClipboardList, Syringe, Pill, Calendar, Loader2, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CATEGORIES = [
  { key: "appointment",  label: "Appointment",  icon: Stethoscope,    color: "from-kawaii-pink to-kawaii-peach" },
  { key: "consultation", label: "Consultation", icon: ClipboardList,  color: "from-kawaii-lavender to-kawaii-pink" },
  { key: "procedure",    label: "Procedure",    icon: Syringe,        color: "from-kawaii-peach to-kawaii-pink" },
  { key: "medication",   label: "Medication",   icon: Pill,           color: "from-kawaii-mint to-kawaii-lavender" },
  { key: "other",        label: "Other",        icon: Calendar,       color: "from-kawaii-lavender to-kawaii-mint" },
];

const catFor = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[0];

export default function FutureDayModal({ dateKey, onClose, onChanged }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [time, setTime] = useState("");
  const [category, setCategory] = useState("appointment");
  const [notes, setNotes] = useState("");
  const titleRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/by-date/${dateKey}`);
      setEvents(data);
    } catch { toast.error("Couldn't load events"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateKey]);

  useEffect(() => {
    if (showForm) setTimeout(() => titleRef.current?.focus(), 50);
  }, [showForm]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Give your event a name ♡");
    setAdding(true);
    try {
      const { data } = await api.post("/events", { date: dateKey, time: time || null, title, category, notes });
      setEvents((prev) => [...prev, data].sort((a, b) => (a.time || "").localeCompare(b.time || "")));
      setTitle(""); setTime(""); setCategory("appointment"); setNotes("");
      setShowForm(false);
      toast.success("Added to your calendar ♡");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save event");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      onChanged && onChanged();
    } catch { toast.error("Couldn't delete"); }
  };

  const displayDate = format(parseISO(dateKey), "EEEE, MMMM d");

  return (
    <motion.div
      className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        layoutId={`day-${dateKey}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-2xl mx-auto my-6 md:my-12 kawaii-card p-6 md:p-10"
        data-testid="future-day-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Upcoming</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-kawaii-text mt-1">{displayDate}</h2>
            <p className="text-sm text-kawaii-textSoft font-body mt-2">
              Plan your appointments, consultations & procedures for this day.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="close-future-modal-btn"
            className="kawaii-btn w-10 h-10 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {loading ? (
            <div className="flex items-center gap-2 text-kawaii-textSoft py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading events...
            </div>
          ) : events.length === 0 && !showForm ? (
            <div className="rounded-2xl border-2 border-dashed border-kawaii-border p-8 text-center">
              <div className="text-4xl mb-2">🌷</div>
              <p className="text-kawaii-textSoft font-body text-sm">Nothing scheduled yet</p>
            </div>
          ) : (
            events.map((ev) => {
              const cat = catFor(ev.category);
              const Icon = cat.icon;
              return (
                <motion.div
                  key={ev.id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-kawaii-border"
                  data-testid={`event-item-${ev.id}`}
                >
                  <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center text-white flex-shrink-0`}>
                    <Icon className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-kawaii-text leading-tight">{ev.title}</h3>
                      {ev.time && (
                        <span className="inline-flex items-center gap-1 text-xs text-kawaii-textSoft font-body">
                          <Clock className="w-3 h-3" strokeWidth={2.5} /> {ev.time}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold mt-1">{cat.label}</div>
                    {ev.notes && <p className="text-sm text-kawaii-text/80 font-body mt-2 leading-relaxed">{ev.notes}</p>}
                  </div>
                  <button
                    onClick={() => remove(ev.id)}
                    data-testid={`delete-event-${ev.id}`}
                    className="kawaii-btn w-8 h-8 rounded-full bg-white border border-kawaii-border flex items-center justify-center flex-shrink-0"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-kawaii-textSoft" />
                  </button>
                </motion.div>
              );
            })
          )}
        </div>

        {showForm ? (
          <motion.form
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="rounded-2xl bg-kawaii-bg/60 border border-kawaii-border p-5 space-y-4"
            data-testid="event-form"
          >
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Title</label>
              <input
                ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. MLD appointment with Sarah"
                data-testid="event-title-input"
                className="mt-2 w-full px-4 py-3 rounded-2xl border border-kawaii-border bg-white font-body text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Time (optional)</label>
                <input
                  type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  data-testid="event-time-input"
                  className="mt-2 w-full px-4 py-3 rounded-2xl border border-kawaii-border bg-white font-body text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Type</label>
                <select
                  value={category} onChange={(e) => setCategory(e.target.value)}
                  data-testid="event-category-select"
                  className="mt-2 w-full px-4 py-3 rounded-2xl border border-kawaii-border bg-white font-body text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                >
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Notes (optional)</label>
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Address, prep instructions, questions to ask..."
                data-testid="event-notes-input"
                className="mt-2 w-full px-4 py-3 rounded-2xl border border-kawaii-border bg-white font-body text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button" onClick={() => { setShowForm(false); setTitle(""); setTime(""); setNotes(""); }}
                className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text"
              >
                Cancel
              </button>
              <button
                type="submit" disabled={adding}
                data-testid="save-event-btn"
                className="kawaii-btn px-5 py-2 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-body font-semibold text-sm shadow-sm disabled:opacity-60 inline-flex items-center gap-2"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
                Save event
              </button>
            </div>
          </motion.form>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-event-btn"
            className="kawaii-btn w-full py-3 rounded-2xl bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold shadow-md inline-flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" strokeWidth={2.5} /> Add an event
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}
